import { X } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";

export interface AutocompleteOption {
  value: string;
  label: string;
}

/**
 * Type-to-filter single-select autocomplete. A text input reveals a filtered
 * option list on focus; picking one fills the input and reports the value, and
 * the inline ✕ clears the selection. Used for admin toolbar filters where a
 * dropdown would be unwieldy (e.g. participant lists).
 */
export function Autocomplete({
  options,
  value,
  onChange,
  placeholder = "Search…",
  emptyMessage = "No matches",
  className,
}: {
  options: AutocompleteOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const select = (val: string) => {
    onChange(val);
    setQuery("");
    setOpen(false);
  };

  // Closed input shows the selection; while open it reflects what's being typed.
  const inputValue = open ? query : (selected?.label ?? "");

  return (
    <div className={cn("relative", className)}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={inputValue}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        // Closing on blur fires when focus leaves the control entirely; option
        // clicks are guarded by the list's onMouseDown preventDefault below.
        onBlur={() => setOpen(false)}
        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-900 outline-none focus:border-ring focus:ring-2 focus:ring-ring"
      />
      {selected && (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => {
            onChange(null);
            setQuery("");
          }}
          className="-translate-y-1/2 absolute top-1/2 right-2 rounded p-0.5 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {open && (
        <div
          role="listbox"
          // Keep the input focused so the click lands before blur closes the list.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-gray-400 text-sm">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => select(o.value)}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50",
                  o.value === value ? "font-medium text-gray-900" : "text-gray-700"
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
